import {
  ArrowPathIcon,
  ArrowUpTrayIcon,
  PhotoIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { Flex, Image, SimpleGrid, Text } from '@mantine/core';
import type {
  DropzoneProps,
  FileRejection,
  FileWithPath
} from '@mantine/dropzone';
import { Dropzone } from '@mantine/dropzone';
import { notifications } from '@mantine/notifications';
import { useEffect, useMemo, useRef, useState } from 'react';

import { notificationStyleConfig } from '@/features/notification/config/notificationConfig';
import { resolveImagePath } from '@/shared/helper/resolveImagePath';
import { translate } from '@/shared/i18n/translate';
import { LBActionButton, LBDropZone } from '@/shared/ui-kit/components';

interface LBPhotoDropZoneProps extends Partial<DropzoneProps> {
  initialValue?: string[];
  onFilesChange?: (files: FileWithPath[]) => void;
  onDelete?: () => Promise<void>;
}

export const LBPhotoDropZone = ({
  initialValue = [],
  children,
  onFilesChange,
  loading,
  onDelete,
  maxFiles,
  ...props
}: LBPhotoDropZoneProps) => {
  const [rejectError, setRejectError] = useState<string | null>(null);
  const [files, setFiles] = useState<FileWithPath[] | string[]>([]);
  const [internalLoading, setInternalLoading] = useState(false);
  const openRef = useRef<() => void>(null);

  useEffect(() => {
    if (!files.length) {
      setFiles(initialValue);
    }
  }, [initialValue]);

  const handleDrop = (acceptedFiles: FileWithPath[]) => {
    setInternalLoading(true);
    setFiles(acceptedFiles);
    if (onFilesChange) {
      onFilesChange(acceptedFiles);
    }
    setInternalLoading(false);
  };

  const handleReject = (rejections: FileRejection[]) => {
    setRejectError(rejections[0].errors[0].message);
    notifications.show({
      title: translate('notifications.rejectedForReason', {
        content: rejections[0].errors[0].message
      }),
      ...notificationStyleConfig
    });
  };

  const getPreviewUrl = (item: string | File) =>
    typeof item === 'string'
      ? resolveImagePath(item)
      : URL.createObjectURL(item);

  const previews = useMemo(
    () =>
      files.slice(0, maxFiles).map((item, idx) => {
        const imageUrl = getPreviewUrl(item);

        return (
          <div key={`preview-${idx}`}>
            <Image
              alt={`preview-${idx}`}
              src={imageUrl}
              onLoad={() => {
                if (typeof item !== 'string') {
                  URL.revokeObjectURL(imageUrl);
                }
              }}
            />
          </div>
        );
      }),
    [files, maxFiles]
  );

  return (
    <LBDropZone
      {...props}
      activateOnClick={!files.length}
      loading={loading || internalLoading}
      maxFiles={maxFiles}
      maxSize={Number(import.meta.env.VITE_PHOTO_MAX_SIZE) * 1024 ** 2}
      openRef={openRef}
      onDrop={handleDrop}
      onReject={handleReject}
    >
      <Dropzone.Accept>
        <ArrowUpTrayIcon color='var(--mantine-color-blue-6)' width={50} />
      </Dropzone.Accept>

      <Dropzone.Reject>
        <Flex align='center' direction='column' py={70}>
          <PhotoIcon color='gray' width={64} />
          <Text c='blue' fz='sm'>
            {rejectError}
          </Text>
        </Flex>
      </Dropzone.Reject>

      {files.length > 0 && (
        <Flex gap='sm' justify='flex-end' mb='sm'>
          <LBActionButton
            pos='absolute'
            right={20}
            style={{ pointerEvents: 'all', zIndex: 99 }}
            top={20}
            onClick={() => openRef.current?.()}
          >
            <ArrowPathIcon width={20} />
          </LBActionButton>
          <LBActionButton
            bottom={20}
            pos='absolute'
            right={20}
            style={{ pointerEvents: 'all', zIndex: 99 }}
            onClick={async () => {
              if (onDelete) {
                await onDelete();
              }
              setFiles([]);
            }}
          >
            <XMarkIcon width={20} />
          </LBActionButton>
        </Flex>
      )}

      {files.length > 1 ? (
        <SimpleGrid cols={3}>{previews}</SimpleGrid>
      ) : files.length ? (
        <div>{previews}</div>
      ) : (
        <Dropzone.Idle>{children}</Dropzone.Idle>
      )}
    </LBDropZone>
  );
};
